import { useCallback, useEffect, useState } from "react";
import { GifResult } from "./api";

const RECENT_KEY = "scrile_msg_gifs_recent";
const FAVORITES_KEY = "scrile_msg_gifs_favorites";
const MAX_RECENT = 24;

function loadList(key: string): GifResult[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as GifResult[];
    }
  } catch (_e) { /* localStorage unavailable */ }
  return [];
}

function saveList(key: string, list: GifResult[]): void {
  try { localStorage.setItem(key, JSON.stringify(list)); } catch (_e) { /* localStorage unavailable */ }
}

// Module-level singletons + pub/sub so multiple picker instances stay in sync.
let recentList: GifResult[] = loadList(RECENT_KEY);
let favoritesList: GifResult[] = loadList(FAVORITES_KEY);
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/**
 * Wipes recent + favorites from memory and localStorage. Call on user logout so
 * the next account that signs in on this device doesn't inherit the previous
 * user's GIF history.
 */
export function clearAllGifHistory(): void {
  recentList = [];
  favoritesList = [];
  try {
    localStorage.removeItem(RECENT_KEY);
    localStorage.removeItem(FAVORITES_KEY);
  } catch (_e) { /* localStorage unavailable */ }
  notify();
}

export default function useGifRecent() {
  const [, setVersion] = useState(0);

  useEffect(() => {
    const l = () => setVersion((v) => v + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const addRecent = useCallback((gif: GifResult) => {
    recentList = [gif, ...recentList.filter((g) => g.id !== gif.id)].slice(0, MAX_RECENT);
    saveList(RECENT_KEY, recentList);
    notify();
  }, []);

  const toggleFavorite = useCallback((gif: GifResult) => {
    favoritesList = favoritesList.some((g) => g.id === gif.id)
      ? favoritesList.filter((g) => g.id !== gif.id)
      : [gif, ...favoritesList];
    saveList(FAVORITES_KEY, favoritesList);
    notify();
  }, []);

  return {
    recent: recentList,
    favorites: favoritesList,
    addRecent,
    toggleFavorite,
  };
}
